import React, { useEffect, useState } from "react";
import { Shell, KpiCard, Section, StatusPill } from "@/components/loomi/Layout";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { LayoutDashboard, FolderKanban, ShieldCheck, UserCircle } from "lucide-react";

export const CLIENT_NAV = [
  { to: "/client", label: "Overview", icon: LayoutDashboard, testId: "nav-client-overview" },
  { to: "/client/projects", label: "My projects", icon: FolderKanban, testId: "nav-client-projects", match: "/client/projects" },
  { to: "/client/profile", label: "Profile", icon: UserCircle, testId: "nav-client-profile" },
];

export default function ClientDashboard() {
  const [kpis, setKpis] = useState(null);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    (async () => {
      const [k, p] = await Promise.all([api.get("/client/kpis"), api.get("/client/projects")]);
      setKpis(k.data); setProjects(p.data);
    })();
  }, []);
  return (
    <Shell title={kpis?.client_name ? `${kpis.client_name} · overview` : "Overview"} portalLabel="Client" nav={CLIENT_NAV}>
      <div className="glass rounded-xl p-3 mb-6 flex items-center gap-3 border border-white/10" data-testid="client-readonly-notice">
        <ShieldCheck className="w-4 h-4 text-indigo-300" />
        <span className="text-xs text-mute">Read-only vendor view · you can view all metrics and logs but cannot modify projects.</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Projects" value={kpis?.projects ?? "—"} accent="primary" testId="client-kpi-projects" />
        <KpiCard label="Live" value={kpis?.live ?? "—"} accent="green" testId="client-kpi-live" />
        <KpiCard label="Starts" value={kpis?.starts ?? "—"} accent="blue" testId="client-kpi-starts" />
        <KpiCard label="Completes" value={kpis?.complete ?? "—"} accent="green" testId="client-kpi-complete" />
        <KpiCard label="Conversion" value={`${kpis?.conversion_pct ?? "—"}%`} accent="amber" testId="client-kpi-conversion" />
      </div>
      <div className="mt-8">
        <Section title="Your projects" testId="section-client-projects">
          {projects.length === 0 ? <div className="text-mute text-sm py-8 text-center" data-testid="client-projects-empty">No projects assigned to your account.</div> : (
            <table className="w-full text-sm" data-testid="client-projects-table">
              <thead><tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4 text-right">Starts</th><th className="py-3 pr-4 text-right">Complete</th><th className="py-3 pr-4 text-right">Term</th><th className="py-3" />
              </tr></thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`client-project-row-${p.id}`}>
                    <td className="py-3 pr-4"><Link to={`/client/projects/${p.id}`} className="font-medium hover:underline" data-testid={`client-project-link-${p.id}`}>{p.name}</Link></td>
                    <td className="py-3 pr-4 uppercase text-xs tracking-widest text-mute">{p.type}</td>
                    <td className="py-3 pr-4"><StatusPill status={p.status} /></td>
                    <td className="py-3 pr-4 text-right font-mono">{p.counters?.starts ?? 0}</td>
                    <td className="py-3 pr-4 text-right font-mono status-complete">{p.counters?.complete ?? 0}</td>
                    <td className="py-3 pr-4 text-right font-mono status-terminate">{p.counters?.terminate ?? 0}</td>
                    <td className="py-3 text-right"><Link className="text-xs text-indigo-300 hover:underline" to={`/client/projects/${p.id}`} data-testid={`client-project-open-${p.id}`}>Open →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </Shell>
  );
}
